import { useMemo, useState } from "react";

import ChartWrapper from "./ChartWrapper";

interface TableColumn {
  align?: "center" | "left" | "right";
  key: string;
  label: string;
  type?: "currency" | "number" | "percentage" | "string";
}

interface TableRow {
  [key: string]: number | string;
}

interface TableChartProps {
  data: {
    columns: TableColumn[];
    rows: TableRow[];
  };
  title: string;
}

type SortDirection = "asc" | "desc" | null;

interface SortState {
  column: string | null;
  direction: SortDirection;
}

export default function TableChart({ title, data }: TableChartProps) {
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: null,
  });

  const handleSort = (columnKey: string) => {
    setSortState((prev) => {
      if (prev.column !== columnKey) {
        return { column: columnKey, direction: "asc" };
      }
      if (prev.direction === "asc") {
        return { column: columnKey, direction: "desc" };
      }
      if (prev.direction === "desc") {
        return { column: null, direction: null };
      }
      return { column: columnKey, direction: "asc" };
    });
  };

  const sortedRows = useMemo(() => {
    if (!data?.rows || !sortState.column || !sortState.direction) {
      return data?.rows || [];
    }

    return [...data.rows].sort((a, b) => {
      const aVal = a[sortState.column!];
      const bVal = b[sortState.column!];

      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortState.direction === "asc" ? 1 : -1;
      if (bVal == null) return sortState.direction === "asc" ? -1 : 1;

      // Compare based on type
      let comparison: number;
      if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortState.direction === "asc" ? comparison : -comparison;
    });
  }, [data?.rows, sortState.column, sortState.direction]);

  const formatValue = (value: number | string, type: string = "string") => {
    if (typeof value === "number") {
      // Cap to 2 decimal places, dropping trailing zeros (e.g. 16.6666 -> 16.67)
      const rounded = Number(value.toFixed(2));
      switch (type) {
        case "currency":
          return `$${rounded.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        case "percentage":
          return `${rounded}%`;
        case "number":
          return rounded.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          });
        default:
          return rounded.toString();
      }
    }
    return value;
  };

  const getAlignment = (type: string = "string", align?: string) => {
    if (align) return align;
    return type === "number" || type === "currency" || type === "percentage"
      ? "right"
      : "left";
  };

  const SortIcon = ({ columnKey }: { columnKey: string }) => {
    const isActive = sortState.column === columnKey;
    const direction = isActive ? sortState.direction : null;

    // Only show sort icon when column is actively sorted
    if (!isActive) {
      return null;
    }

    return (
      <span className="ml-1 inline-flex flex-col">
        {direction === "asc" && (
          <svg
            className="h-2 w-2 text-gray-900"
            viewBox="0 0 8 5"
            fill="currentColor"
          >
            <path d="M4 0L8 5H0L4 0Z" />
          </svg>
        )}
        {direction === "desc" && (
          <svg
            className="h-2 w-2 text-gray-900"
            viewBox="0 0 8 5"
            fill="currentColor"
          >
            <path d="M4 5L0 0H8L4 5Z" />
          </svg>
        )}
      </span>
    );
  };

  // Safety check for undefined/null data
  if (!data || !data.columns || !data.rows) {
    return (
      <ChartWrapper title={title}>
        <div className="flex items-center justify-center h-full">
          <div className="text-sm text-gray-500">No data available</div>
        </div>
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper title={title}>
      <div className="overflow-hidden h-full">
        <div className="overflow-auto h-full">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="border-b border-gray-200">
                {data.columns.map((column) => (
                  <th
                    key={column.key}
                    onClick={() => handleSort(column.key)}
                    className={`px-3 py-2 font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-100 transition-colors text-${getAlignment(column.type, column.align)}`}
                  >
                    <span className="inline-flex items-center">
                      {column.label}
                      <SortIcon columnKey={column.key} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  {data.columns.map((column) => (
                    <td
                      key={column.key}
                      title={
                        typeof row[column.key] === "number"
                          ? String(row[column.key])
                          : undefined
                      }
                      className={`px-3 py-2 text-gray-900 text-${getAlignment(column.type, column.align)}`}
                    >
                      {formatValue(row[column.key], column.type)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </ChartWrapper>
  );
}
